import { useState, useEffect, useMemo } from 'react'
import { useLanguage } from '@/i18n/LanguageContext'
import { useQueryClient } from '@tanstack/react-query'
import { cn } from '@/lib/utils'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { BarChart3, RefreshCw, Eye, Layers, AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'
import {
  useViewAnalytics, useEnsureSnapshot, type FinancePage,
} from '@/api/hooks/usePages'

const LINE_COLORS = [
  '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
  '#ec4899', '#06b6d4', '#f97316', '#14b8a6', '#6366f1',
]

const fmtNum = new Intl.NumberFormat('pt-BR')

interface Props {
  pages: FinancePage[]
}

export function VideoAnalytics({ pages }: Props) {
  const { t } = useLanguage()
  const qc = useQueryClient()

  const eligiblePages = useMemo(
    () => pages.filter(p => p.hasToken && p.fbPageId),
    [pages]
  )

  // Empty = "all" (no filter). Non-empty = filter to those specific pages.
  const [filterIds, setFilterIds] = useState<string[]>([])
  const [snapshotError, setSnapshotError] = useState(false)

  // The actual page IDs sent to the API: if no filter, use all eligible
  const activeIds = useMemo(
    () => filterIds.length > 0 ? filterIds : eligiblePages.map(p => p.id),
    [filterIds, eligiblePages]
  )

  const { data: analytics, isLoading, isFetching } = useViewAnalytics(activeIds)
  const ensureMut = useEnsureSnapshot()

  const toggleFilter = (id: string) => {
    setFilterIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    )
  }

  // Ensure fresh snapshots on mount and when active pages change
  // Process sequentially to avoid hammering the Meta API
  // Invalidate view-analytics only ONCE after all snapshots complete
  useEffect(() => {
    if (activeIds.length === 0) return
    let cancelled = false
    setSnapshotError(false)
    const run = async () => {
      let anyFresh = false
      let failures = 0
      for (const id of activeIds) {
        if (cancelled) break
        try {
          const result = await ensureMut.mutateAsync(id)
          if (result.fresh) anyFresh = true
        } catch {
          failures++
        }
      }
      if (cancelled) return
      if (anyFresh) {
        qc.invalidateQueries({ queryKey: ['view-analytics'] })
      }
      if (failures === activeIds.length) {
        setSnapshotError(true)
        toast.error(t('Erro ao capturar snapshots. Verifique os access tokens das paginas.'))
      }
    }
    run()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIds.join(',')])

  // Transform analytics data into recharts format
  const chartData = useMemo(() => {
    if (!analytics || analytics.length === 0) return []

    const allTimestamps = new Map<number, string>()
    analytics.forEach(a => {
      a.dataPoints.forEach(dp => {
        allTimestamps.set(dp.timestamp, dp.time)
      })
    })

    const sorted = Array.from(allTimestamps.entries()).sort((a, b) => a[0] - b[0])

    return sorted.map(([ts, timeLabel]) => {
      const point: Record<string, unknown> = { time: timeLabel, timestamp: ts }
      analytics.forEach(a => {
        const match = a.dataPoints.find(dp => dp.timestamp === ts)
        point[a.pageName] = match?.views ?? 0
      })
      return point
    })
  }, [analytics])

  const pageNames = analytics?.map(a => a.pageName) ?? []

  // Summary stats
  const totalViewsToday = useMemo(() => {
    if (!analytics) return 0
    return analytics.reduce((sum, a) => {
      return sum + a.dataPoints.reduce((s, dp) => s + dp.views, 0)
    }, 0)
  }, [analytics])

  const peakWindow = useMemo(() => {
    if (!analytics) return null
    let maxViews = 0
    let maxTime = ''
    analytics.forEach(a => {
      a.dataPoints.forEach(dp => {
        if (dp.views > maxViews) {
          maxViews = dp.views
          maxTime = dp.time
        }
      })
    })
    return maxViews > 0 ? { time: maxTime, views: maxViews } : null
  }, [analytics])

  const isFiltering = filterIds.length > 0

  return (
    <div className="space-y-4">
      {/* Page filter chips */}
      {eligiblePages.length > 0 && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-primary" />
              {t('Analytics de Views')}
            </h3>
            {isFetching && (
              <Badge variant="secondary" className="text-[10px] gap-1">
                <RefreshCw className="h-3 w-3 animate-spin" /> {t('Atualizando...')}
              </Badge>
            )}
          </div>

          <div className="flex flex-wrap gap-1.5">
            {/* "Todas" chip */}
            <button
              type="button"
              onClick={() => setFilterIds([])}
              className={cn(
                'flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors border',
                !isFiltering
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-muted-foreground border-border hover:text-foreground hover:border-foreground/30',
              )}
            >
              <Layers className="h-3 w-3" /> {t('Todas')} ({eligiblePages.length})
            </button>

            {/* Individual page chips */}
            {eligiblePages.map((page, i) => {
              const isActive = filterIds.includes(page.id)
              const colorIdx = isActive ? filterIds.indexOf(page.id) : i
              return (
                <button
                  key={page.id}
                  type="button"
                  onClick={() => toggleFilter(page.id)}
                  className={cn(
                    'flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors border',
                    isActive
                      ? 'bg-primary/10 text-foreground border-primary/50'
                      : 'bg-background text-muted-foreground border-border hover:text-foreground hover:border-foreground/30',
                  )}
                >
                  <div
                    className="h-2.5 w-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: LINE_COLORS[colorIdx % LINE_COLORS.length] }}
                  />
                  {page.name}
                </button>
              )
            })}
          </div>
        </Card>
      )}

      {eligiblePages.length === 0 && (
        <Card className="p-8 text-center">
          <BarChart3 className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            {t('Nenhuma pagina com access token configurado. Importe suas contas ou adicione um token nas paginas.')}
          </p>
        </Card>
      )}

      {/* Stats + Chart (always visible when there are eligible pages) */}
      {activeIds.length > 0 && (
        <>
          {/* Summary stats */}
          {analytics && analytics.some(a => a.dataPoints.length > 0) && (
            <div className="grid gap-3 sm:grid-cols-3">
              <Card className="p-3 flex items-center gap-3">
                <Eye className="h-5 w-5 text-blue-500" />
                <div>
                  <div className="text-[10px] text-muted-foreground">{t('Views (24h)')}</div>
                  <div className="text-lg font-bold">{fmtNum.format(totalViewsToday)}</div>
                </div>
              </Card>
              <Card className="p-3 flex items-center gap-3">
                <BarChart3 className="h-5 w-5 text-emerald-500" />
                <div>
                  <div className="text-[10px] text-muted-foreground">{t('Pico')}</div>
                  <div className="text-lg font-bold">
                    {peakWindow ? `+${fmtNum.format(peakWindow.views)}` : '-'}
                  </div>
                  {peakWindow && (
                    <div className="text-[10px] text-muted-foreground">{t('as')} {peakWindow.time}</div>
                  )}
                </div>
              </Card>
              <Card className="p-3 flex items-center gap-3">
                <Layers className="h-5 w-5 text-purple-500" />
                <div>
                  <div className="text-[10px] text-muted-foreground">{t('Paginas monitoradas')}</div>
                  <div className="text-lg font-bold">{activeIds.length}</div>
                </div>
              </Card>
            </div>
          )}

          {/* Chart */}
          <Card className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold">
                {t('Views ganhas a cada 2 horas (ultimas 24h)')}
                {isFiltering && (
                  <span className="text-muted-foreground font-normal ml-1.5">
                    — {filterIds.length} {filterIds.length === 1 ? t('pagina') : t('paginas')}
                  </span>
                )}
              </h3>
            </div>

            {isLoading ? (
              <Skeleton className="h-[300px] rounded" />
            ) : snapshotError ? (
              <div className="flex flex-col items-center justify-center h-[300px] text-center">
                <AlertTriangle className="h-12 w-12 text-destructive/40 mb-3" />
                <p className="text-sm text-destructive font-medium">
                  {t('Erro ao capturar dados')}
                </p>
                <p className="text-xs text-muted-foreground mt-1 max-w-[300px]">
                  {t('Verifique se os access tokens das paginas estao validos. Os tokens expiram periodicamente e precisam ser renovados.')}
                </p>
              </div>
            ) : chartData.length < 2 ? (
              <div className="flex flex-col items-center justify-center h-[300px] text-center">
                <BarChart3 className="h-12 w-12 text-muted-foreground/30 mb-3" />
                <p className="text-sm text-muted-foreground font-medium">
                  {t('Coletando dados...')}
                </p>
                <p className="text-xs text-muted-foreground mt-1 max-w-[300px]">
                  {t('O sistema captura snapshots a cada 2 horas. O primeiro relatorio aparecera em algumas horas.')}
                </p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis
                    dataKey="time"
                    tick={({ x, y, payload }) => (
                      <text x={x} y={y} dy={14} textAnchor="middle" style={{ fill: 'var(--muted-foreground)', fontSize: 11 }}>
                        {payload.value}
                      </text>
                    )}
                    tickLine={{ stroke: 'var(--muted-foreground)' }}
                    axisLine={{ stroke: 'var(--muted-foreground)' }}
                  />
                  <YAxis
                    tick={({ x, y, payload }) => {
                      const v = Number(payload.value)
                      const label = v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v)
                      return (
                        <text x={x} y={y} dx={-4} textAnchor="end" style={{ fill: 'var(--muted-foreground)', fontSize: 11 }}>
                          {label}
                        </text>
                      )
                    }}
                    tickLine={{ stroke: 'var(--muted-foreground)' }}
                    axisLine={{ stroke: 'var(--muted-foreground)' }}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload) return null
                      return (
                        <div className="rounded-lg border bg-background p-2.5 text-xs shadow-md text-foreground">
                          <div className="font-semibold mb-1">{label}</div>
                          {payload.map((p) => (
                            <div key={p.name} className="flex items-center gap-1.5" style={{ color: String(p.color) }}>
                              <div className="h-2 w-2 rounded-full" style={{ backgroundColor: String(p.color) }} />
                              {p.name}: <span className="font-medium">+{fmtNum.format(Number(p.value))}</span> views
                            </div>
                          ))}
                        </div>
                      )
                    }}
                  />
                  <Legend
                    content={({ payload }) => (
                      <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-[11px] text-foreground mt-2">
                        {(payload || []).map((entry) => (
                          <span key={String(entry.value)} className="inline-flex items-center gap-1.5">
                            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: String(entry.color) }} />
                            {entry.value}
                          </span>
                        ))}
                      </div>
                    )}
                  />
                  {pageNames.map((name, i) => (
                    <Line
                      key={name}
                      type="monotone"
                      dataKey={name}
                      stroke={LINE_COLORS[i % LINE_COLORS.length]}
                      strokeWidth={2}
                      dot={{ fill: LINE_COLORS[i % LINE_COLORS.length], r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </Card>
        </>
      )}
    </div>
  )
}
